import { isOfflineCapable, visibleFields } from "@domain/form";
import {
	Alert,
	Card,
	DatePicker,
	Divider,
	Empty,
	Form,
	Input,
	InputNumber,
	Rate,
	Select,
	Switch,
	Typography,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { FormAnswers, FormField } from "../types";

interface Props {
	fields: FormField[];
}

/**
 * ⚠️ **Ko'rinuvchanlik `@domain/form` ning `visibleFields()` idan.**
 * O'z nusxasi YOZILMAYDI: mobil ilova ham, server validatori ham AYNAN
 * shu funksiyani chaqiradi (spec §4.3 invariant 3, §7.3). Nusxa
 * yozilsa, oldindan ko'rish va haqiqiy ilova jimgina ajralib ketardi.
 */
function PreviewInput({
	field,
	value,
	onChange,
}: {
	field: FormField;
	value: unknown;
	onChange: (next: unknown) => void;
}) {
	const { t } = useTranslation("leadForms");

	switch (field.type) {
		case "textarea":
			return (
				<Input.TextArea
					rows={3}
					value={(value as string) ?? ""}
					onChange={(e) => onChange(e.target.value)}
				/>
			);
		case "number":
			return <InputNumber value={(value as number) ?? null} onChange={(next) => onChange(next)} />;
		case "rating":
			return <Rate count={field.max ?? 5} value={(value as number) ?? 0} onChange={onChange} />;
		case "boolean":
			return <Switch checked={value === true} onChange={onChange} />;
		case "select":
			return (
				<Select
					className="w-full"
					value={(value as string) ?? undefined}
					options={(field.options ?? []).map((o) => ({ value: o.value, label: o.label }))}
					onChange={onChange}
				/>
			);
		case "multiselect":
			return (
				<Select
					className="w-full"
					mode="multiple"
					value={(value as string[]) ?? []}
					options={(field.options ?? []).map((o) => ({ value: o.value, label: o.label }))}
					onChange={onChange}
				/>
			);
		case "date":
			return (
				<DatePicker
					className="w-full"
					value={typeof value === "string" && value ? dayjs(value) : null}
					onChange={(next) => onChange(next ? next.format("YYYY-MM-DD") : undefined)}
				/>
			);
		case "datetime":
			return (
				<DatePicker
					className="w-full"
					showTime
					value={typeof value === "string" && value ? dayjs(value) : null}
					onChange={(next) => onChange(next ? next.toISOString() : undefined)}
				/>
			);
		case "time":
			return (
				<Input
					placeholder="HH:MM"
					value={(value as string) ?? ""}
					onChange={(e) => onChange(e.target.value)}
				/>
			);
		case "file":
		case "image":
		case "signature":
			// v1 da faqat onlayn (spec §4.2, R6) — oldindan ko'rishda
			// haqiqiy yuklash oqimi qurilmaydi, faqat joyi ko'rsatiladi.
			return <Alert type="info" showIcon message={t("preview.uploadOnlineOnly")} />;
		case "geopoint":
			return <Alert type="info" showIcon message={t("preview.geopointOnlineOnly")} />;
		default:
			return <Input value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} />;
	}
}

export function FormPreview({ fields }: Props) {
	const { t } = useTranslation("leadForms");
	const [answers, setAnswers] = useState<FormAnswers>({});

	if (fields.length === 0) {
		return <Empty description={t("preview.empty")} />;
	}

	// ⚠️ AYNAN mobil ilovaning funksiyasi. Tartib saqlanadi.
	const visible = visibleFields({ fields }, answers);
	const offline = isOfflineCapable({ fields });

	const setAnswer = (key: string, value: unknown) =>
		setAnswers((current) => ({ ...current, [key]: value }));

	return (
		<Card size="small" title={t("preview.title")}>
			{!offline && (
				<Alert
					type="warning"
					showIcon
					className="mb-3"
					message={t("preview.offlineWarningTitle")}
					description={t("preview.offlineWarningDescription")}
				/>
			)}
			<Form layout="vertical">
				{visible.map((field) =>
					field.type === "section" ? (
						<Divider key={field.key} titlePlacement="left">
							<Typography.Text strong>{field.label}</Typography.Text>
						</Divider>
					) : (
						<Form.Item
							key={field.key}
							label={field.label}
							required={field.required}
							help={field.helpText}
						>
							<PreviewInput
								field={field}
								value={answers[field.key]}
								onChange={(value) => setAnswer(field.key, value)}
							/>
							{/*
							 * Test seami: Ant Design'ning `Select`/`Switch`
							 * komponentlari klik oqimini talab qiladi va
							 * happy-dom'da ularni boshqarish mo'rt. Yashirin
							 * `input` javobni to'g'ridan-to'g'ri o'zgartirishga
							 * imkon beradi — shartli ko'rinuvchanlikni pinlash
							 * uchun aynan shu kerak.
							 */}
							<input
								type="hidden"
								data-testid={`answer-${field.key}`}
								onInput={(event) => {
									const raw = (event.target as HTMLInputElement).value;

									setAnswer(field.key, raw === "true" ? true : raw === "false" ? false : raw);
								}}
							/>
						</Form.Item>
					)
				)}
			</Form>
		</Card>
	);
}
